import {checkSignal, requestJSON, safeMediaURL} from './direct';

const pause = (ms, signal) => new Promise((resolve, reject) => {
  checkSignal(signal);
  const abort = () => {clearTimeout(timer); reject(new DOMException('Ожидание отменено', 'AbortError'));};
  const timer = setTimeout(() => {signal?.removeEventListener('abort', abort); resolve();}, ms);
  signal?.addEventListener('abort', abort, {once: true});
});

// ZIP bytes are assembled asynchronously by the main server and downloaded by
// native navigation. Account credentials never enter the signed download URL.
export async function runArchive(apiURL, token, selection, {signal, onUpdate, share = '', audience = 'staff'} = {}) {
  if (!token && !share) throw new Error('Войдите в кабинет, чтобы скачать архив.');
  const base = apiURL.replace(/\/$/, '');
  const headers = {...(token ? {Authorization: `${audience === 'client' ? 'ClientToken' : 'Bearer'} ${token}`} : {}),
    ...(share ? {'X-Storage-Share': share} : {})};
  const call = (path, data) => requestJSON(base + path, {
    method: data ? 'POST' : 'GET', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
    headers: {...headers, ...(data ? {'Content-Type': 'application/json'} : {})},
    ...(data ? {body: JSON.stringify(data)} : {}),
  }, signal);
  let job = await call('/main/api/media/archives/', selection);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(job?.id || ''))) throw new Error('Сервер вернул некорректный ID архива.');
  const identity = job.id;
  const statusPath = `/main/api/media/archives/${identity}/`;
  for (;;) {
    checkSignal(signal);
    if (job.status === 'ready') {
      let url;
      try {
        url = archiveDownloadURL(base, job.download_url, identity);
      } catch (failure) {
        onUpdate?.({...job, notice: 'ZIP подготовлен. Не удалось начать скачивание — повторите попытку.'});
        throw failure;
      }
      checkSignal(signal);
      const anchor = document.createElement('a');
      anchor.href = url.href; anchor.rel = 'noopener noreferrer'; anchor.referrerPolicy = 'no-referrer';
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      onUpdate?.(job);
      return job;
    }
    if (['failed', 'error', 'expired', 'cancelled'].includes(job.status)) {
      onUpdate?.(job);
      const error = new Error(job.error || job.detail || 'Архив не удалось подготовить. Повторите попытку.');
      error.code = job.code; throw error;
    }
    if (!['pending', 'building', 'processing', 'queued', 'running'].includes(job.status)) {
      throw new Error('Неизвестный статус подготовки архива. Обновите страницу.');
    }
    onUpdate?.(job);
    await pause(1500, signal);
    for (let attempt = 0; ; attempt++) {
      try {job = await call(statusPath); break;}
      catch (failure) {
        checkSignal(signal);
        if (attempt >= 3 || failure.status && ![408, 425, 429, 500, 502, 503, 504].includes(failure.status)) throw failure;
        onUpdate?.({...job, notice: 'Связь прервалась. Проверяем тот же архив…'});
        await pause(1500 * (attempt + 1), signal);
      }
    }
    if (String(job?.id) !== String(identity)) throw new Error('Изменился ID архива.');
  }
}

export function archiveStatus(job) {
  if (!job) return 'Подготавливаем архив…';
  if (job.notice) return job.notice;
  if (job.status === 'ready') return 'Архив готов. Запрос на скачивание отправлен.';
  if (['failed', 'error', 'cancelled'].includes(job.status)) return 'Подготовка ZIP остановлена.';
  if (job.status === 'expired') return 'Срок хранения ZIP истёк. Подготовьте архив повторно.';
  const done = Number(job.done_files ?? job.completed_files ?? job.processed_files ?? job.progress?.files ?? 0);
  const total = Number(job.files ?? job.total_files ?? job.file_count ?? job.progress?.total ?? 0);
  return total > 0 ? `Подготавливаем ZIP: ${Math.min(done, total)} из ${total} файлов` : 'Архив в очереди на подготовку…';
}

// Resolve relative capabilities against the authenticated API, then require the
// exact endpoint and signed ticket. No credentials are sent to a foreign host.
export function archiveDownloadURL(apiURL, value, identity) {
  try {
    if (typeof value !== 'string' || !value.trim()) throw new Error();
    const base = apiURL.replace(/\/$/, '');
    const expected = new URL(`${base}/main/api/media/archives/${identity}/download/`);
    const url = safeMediaURL(new URL(value, base + '/').href);
    if (url.origin !== expected.origin || url.pathname !== expected.pathname || url.hash ||
        url.searchParams.getAll('ticket').length !== 1 || !url.searchParams.get('ticket') ||
        [...url.searchParams.keys()].some(key => key !== 'ticket')) throw new Error();
    return url;
  } catch (_) {
    throw new Error('Сервер вернул некорректную ссылку на архив.');
  }
}
